import { useCallback, useEffect, useMemo, useState } from 'react'
import { getMicroLearningDetail } from '../../../api/api'
import { MicroLearningDetailResponse, ReviewDecision } from '../../../api/models'

type UseMicroLearningDetailResult = {
  isLoading: boolean
  hasError: boolean
  isNotFound: boolean
  data: MicroLearningDetailResponse | null
  showScalePotential: boolean
  notProcessed: boolean
  reviewDecision: ReviewDecision | null
  refetch: () => void
  patchData: (partial: Partial<MicroLearningDetailResponse>) => void
}

export const useMicroLearningDetail = (mlId: string): UseMicroLearningDetailResult => {
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isNotFound, setIsNotFound] = useState(false)
  const [data, setData] = useState<MicroLearningDetailResponse | null>(null)
  const [fetchCount, setFetchCount] = useState(0)

  const refetch = useCallback(() => setFetchCount(c => c + 1), [])

  useEffect(() => {
    if (!mlId) {
      setIsLoading(false)
      setHasError(true)
      setIsNotFound(false)
      setData(null)
      return
    }

    let isSubscribed = true
    const controller = new AbortController()

    const loadDetail = async () => {
      setIsLoading(true)
      const response = await getMicroLearningDetail(mlId, { signal: controller.signal })
      if (!isSubscribed) return

      setIsLoading(false)
      if (!response.data) {
        setData(null)
        setIsNotFound(response.status === 404)
        setHasError(true)
        return
      }

      setIsNotFound(false)
      setHasError(false)
      setData(response.data)
    }

    loadDetail()

    return () => {
      isSubscribed = false
      controller.abort()
    }
  }, [mlId, fetchCount])

  const showScalePotential = useMemo(
    () => (data?.experiments_scale_potential || []).length > 0,
    [data?.experiments_scale_potential],
  )

  const notProcessed = data?.status === 'not_processed' || !data?.generation

  const reviewDecision = data?.review_decision ?? null

  const patchData = useCallback(
    (partial: Partial<MicroLearningDetailResponse>) =>
      setData(prev => (prev ? { ...prev, ...partial } : prev)),
    [],
  )

  return {
    isLoading,
    hasError,
    isNotFound,
    data,
    showScalePotential,
    notProcessed,
    reviewDecision,
    refetch,
    patchData,
  }
}
